import React, { useCallback, useEffect, useRef, useState } from "react";
import { LifeBuoy } from "lucide-react";
import ReportIssueDialog from "../../components/ReportIssueDialog";

/* ───────── ReportFab ─────────
   Draggable "Report an issue" floating action button.

   Behaviour:
     • Press-and-drag anywhere on screen (works on mouse + touch)
     • Position persists in localStorage across navigations + reloads
     • Constrained to stay inside the visible viewport (incl. safe-area)
     • Click-vs-drag disambiguation via a 6 px movement threshold — a
       quick tap still opens the dialog; only deliberate drags reposition
     • If the viewport shrinks (rotation, mobile keyboard) and the FAB
       would land off-screen, we clamp it back into bounds on next render

   Storage key: `lw.reportFab.pos` — small `{x, y}` blob, ~30 bytes. */

const STORAGE_KEY = "lw.reportFab.pos";
const DRAG_THRESHOLD = 6;        // px before we consider the gesture a drag
const EDGE_PADDING = 8;          // visible margin from viewport edges
const DEFAULT_BOTTOM_OFFSET = 100;
const DEFAULT_RIGHT_OFFSET = 18;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function loadStoredPos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") return null;
    return parsed;
  } catch (_) { return null; }
}

function defaultPos(buttonSize) {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: vw - buttonSize.w - DEFAULT_RIGHT_OFFSET,
    y: vh - buttonSize.h - DEFAULT_BOTTOM_OFFSET,
  };
}

export default function ReportFab() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  // Use `null` while we resolve the initial position so we don't flash
  // top-left on first paint before measuring the button.
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  // We keep drag-start metadata in a ref to avoid re-rendering on every
  // pointermove event; only the final position update goes through state.
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  // First mount: pick stored position or default to bottom-right.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = buttonRef.current;
    const size = el
      ? { w: el.offsetWidth, h: el.offsetHeight }
      : { w: 140, h: 44 };
    const stored = loadStoredPos();
    const initial = stored || defaultPos(size);
    // Clamp to viewport in case the stored value came from a wider screen.
    setPos({
      x: clamp(initial.x, EDGE_PADDING, window.innerWidth  - size.w - EDGE_PADDING),
      y: clamp(initial.y, EDGE_PADDING, window.innerHeight - size.h - EDGE_PADDING),
    });
  }, []);

  // Re-clamp when the viewport changes (rotation, mobile keyboard, window resize).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const el = buttonRef.current;
      if (!el || !pos) return;
      const size = { w: el.offsetWidth, h: el.offsetHeight };
      const next = {
        x: clamp(pos.x, EDGE_PADDING, window.innerWidth  - size.w - EDGE_PADDING),
        y: clamp(pos.y, EDGE_PADDING, window.innerHeight - size.h - EDGE_PADDING),
      };
      if (next.x !== pos.x || next.y !== pos.y) setPos(next);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [pos]);

  const persist = useCallback((p) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); }
    catch (_) { /* private mode / quota — ignore */ }
  }, []);

  const onPointerDown = useCallback((e) => {
    if (!pos) return;
    // Only handle primary mouse button (or any touch).
    if (e.button !== undefined && e.button !== 0) return;
    const el = buttonRef.current;
    if (!el) return;
    el.setPointerCapture && el.setPointerCapture(e.pointerId);
    dragRef.current = {
      active: true, moved: false,
      startX: e.clientX, startY: e.clientY,
      originX: pos.x,    originY: pos.y,
    };
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!d.moved) { d.moved = true; setDragging(true); }
    const el = buttonRef.current;
    const size = { w: el.offsetWidth, h: el.offsetHeight };
    setPos({
      x: clamp(d.originX + dx, EDGE_PADDING, window.innerWidth  - size.w - EDGE_PADDING),
      y: clamp(d.originY + dy, EDGE_PADDING, window.innerHeight - size.h - EDGE_PADDING),
    });
    e.preventDefault?.();
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const wasDrag = d.moved;
    d.active = false;
    setDragging(false);
    if (wasDrag) {
      // Persist final position and SWALLOW the synthetic click so a drag
      // never opens the dialog by accident.
      if (pos) persist(pos);
      e.preventDefault?.();
      e.stopPropagation?.();
    } else {
      // No movement — treat as click → open dialog.
      setOpen(true);
    }
  }, [pos, persist]);

  // Don't render until we know where to put it (avoids flash-of-mispositioned).
  if (!pos) return <ReportIssueDialog open={open} onClose={() => setOpen(false)} kind="general" />;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Report an issue (drag to reposition)"
        title="Drag to reposition · tap to open"
        data-testid="report-fab"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        // Block the native click when we just finished a drag — without
        // this, the synthetic click after pointerup would still open the
        // dialog despite the user only intending to reposition the FAB.
        onClick={(e) => { if (dragRef.current.moved) { e.preventDefault(); e.stopPropagation(); dragRef.current.moved = false; } }}
        className="fixed z-[70] inline-flex items-center gap-2 select-none transition-transform"
        style={{
          left: pos.x,
          top: pos.y,
          height: 44,
          padding: "0 14px 0 12px",
          borderRadius: 999,
          background: "#0b0b0d",
          border: "1.5px solid #0b0b0d",
          boxShadow: dragging
            ? "0 18px 40px -8px rgba(11,11,13,0.55)"
            : "0 12px 28px -6px rgba(11,11,13,0.35)",
          color: "#ffffff",
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: 0.2,
          touchAction: "none",          // critical: lets pointer-events drive drag on touch
          cursor: dragging ? "grabbing" : "grab",
          transform: dragging ? "scale(1.05)" : "scale(1)",
          // No CSS transition on left/top — that would lag the drag.
        }}
      >
        <LifeBuoy size={15} />
        <span className="hidden md:inline">Report an issue</span>
      </button>
      <ReportIssueDialog open={open} onClose={() => setOpen(false)} kind="general" />
    </>
  );
}
